import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { LangProvider } from './i18n';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './AuthContext';
import AppShell from './components/AppShell';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import Register from './pages/Register';
import Demo from './pages/Demo';
import Planning from './pages/Planning';
import Today from './pages/Today';
import MessagesTypes from './pages/MessagesTypes';
import Students from './pages/Students';
import StudentProfile from './pages/StudentProfile';
import Finance from './pages/Finance';
import TeacherProfile from './pages/TeacherProfile';
import GroupClasses from './pages/GroupClasses';
import GroupClassDetail from './pages/GroupClassDetail';
import PrivateSessions from './pages/PrivateSessions';
import Festivals from './pages/Festivals';
import Habits from './pages/Habits';
import PlannerPublic from './pages/PlannerPublic';

// Role-based access config
const ROLE_ACCESS = {
  admin:     ['/', '/messages-types', '/planning', '/group-classes', '/private-sessions', '/students', '/finance', '/profile', '/festivals', '/habits'],
  assistant: ['/group-classes'],
  viewer:    ['/group-classes'],
};

function canAccess(role, path) {
  if (!role) return false;
  if (role === 'admin') return true;
  const allowed = ROLE_ACCESS[role] || [];
  return allowed.some(p => path === p || path.startsWith(p + '/'));
}

function ProtectedRoute({ children }) {
  const { user, effectiveRole } = useAuth();
  const location = useLocation();

  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-10 h-10 border-4 border-[#0288D1] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (!canAccess(user.role, location.pathname)) {
    return <Navigate to="/group-classes" replace />;
  }
  return children;
}

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      {/* Public routes — no auth required */}
      <Route path="/register" element={<Register />} />
      <Route path="/planificateur" element={<PlannerPublic />} />
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/demo/:token" element={<Demo />} />

      <Route path="/*" element={
        <ProtectedRoute>
          <AppShell>
            <Routes>
              <Route path="/" element={
                user?.role === 'admin'
                  ? <Navigate to="/aujourdhui" replace />
                  : <Navigate to="/group-classes" replace />
              } />
              <Route path="/aujourdhui" element={user?.role === 'admin' ? <Today /> : <Navigate to="/" replace />} />
              <Route path="/planning" element={<Planning />} />
              <Route path="/group-classes" element={<GroupClasses />} />
              <Route path="/group-classes/:id" element={<GroupClassDetail />} />
              <Route path="/private-sessions" element={<PrivateSessions />} />
              <Route path="/students" element={<Students />} />
              <Route path="/students/:id" element={<StudentProfile />} />
              <Route path="/finance" element={<Finance />} />
              <Route path="/profile" element={<TeacherProfile />} />
              <Route path="/festivals" element={<Festivals />} />
              <Route path="/habits" element={<Habits />} />
              <Route path="/messages-types" element={<MessagesTypes />} />
              <Route path="/planner" element={<PlannerPublic embedded isAdmin />} />
            </Routes>
          </AppShell>
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <LangProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </LangProvider>
  );
}

export default App;
